import { useEffect, useState, useCallback, useMemo } from 'react';
import { Autocomplete, Box, Typography, Paper, CircularProgress, Button, Stack, useMediaQuery, useTheme, Collapse, Grid, IconButton, Tooltip, Snackbar, Alert, Checkbox, Card, CardContent, Avatar, TextField, InputAdornment, Divider, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Radio, RadioGroup, Popover, LinearProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Chip } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { VscNewFile } from 'react-icons/vsc';
import { FaUser } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useRights } from '../context/RightsContext';
import useDataLoadingState from '../hooks/useDataLoadingState';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatRows } from '../utils/formatRows';
import NoDataComponent from '../components/NoDataComponent';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import CenterLoader from '../components/CenterLoader';
import QRCode from 'qrcode';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import EwayBillMaster from './EwayBill';
import EInvoiceMaster from './EInvoice';
import * as XLSX from 'xlsx';

const normalizeDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split("-").reverse().join("/") : v;
const transform = (data, idKey, valKey) => (Array.isArray(data) ? data : []).map(item => ({ id: item?.[idKey], label: item?.[valKey] }));
const getFinancialYearRange = (now = new Date()) => {
    const month = now.getMonth(); // 0-based
    const startYear = month >= 3 ? now.getFullYear() : now.getFullYear() - 1; // FY starts April
    const from = `${startYear}-04-01`;
    const to = `${startYear + 1}-03-31`;
    return { from, to };
};

// Avatar helpers
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4',];
    return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
    if (!name) return 'I';
    return name.trim().charAt(0).toUpperCase();
}

export default function SaleInvoice_v2({ connectionString, pdfString }) {
    const PDF_API_BASE = pdfString;
    const { user, selectedCompany } = useAuth();
    const { canAccess, showAccessDenied } = useRights() || {};
    const API_URL = `${connectionString}/api`;
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(500));

    const [challanRows, setChallanRows] = useState([]);
    const [challanLoading, setChallanLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [invoiceRows, setInvoiceRows] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceSelectionModel, setInvoiceSelectionModel] = useState([]);
    const [selectedChallanRows, setSelectedChallanRows] = useState([]);
    const [selectedChallanIds, setSelectedChallanIds] = useState([]);

    const [filterText, setFilterText] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Filter popup (UI only; does not change existing logic)
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterPopupMode, setFilterPopupMode] = useState('datewise'); // datewise | partywise
    const [filterPopupFromDate, setFilterPopupFromDate] = useState('');
    const [filterPopupToDate, setFilterPopupToDate] = useState('');
    const [filterPopupFirms, setFilterPopupFirms] = useState([]);
    const [filterPopupSaleAcs, setFilterPopupSaleAcs] = useState([]);
    const [filterPopupSeries, setFilterPopupSeries] = useState([]);
    const [firmOptions, setFirmOptions] = useState([]);
    const [firmAllOptions, setFirmAllOptions] = useState([]);
    const [saleAcOptions, setSaleAcOptions] = useState([]);
    const [saleAcAllOptions, setSaleAcAllOptions] = useState([]);
    const [seriesOptions, setSeriesOptions] = useState([]);
    const [firmInputValue, setFirmInputValue] = useState('');
    const [saleAcInputValue, setSaleAcInputValue] = useState('');
    const [firmLoading, setFirmLoading] = useState(false);
    const [saleAcLoading, setSaleAcLoading] = useState(false);
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [filterPopupExportCancelledOnly, setFilterPopupExportCancelledOnly] = useState(false);
    const [filterPopupSnapshot, setFilterPopupSnapshot] = useState(null);
    const [exportExcelEnabled, setExportExcelEnabled] = useState(false);

    const [pdfLoading, setPdfLoading] = useState(false);
    const [loadingPdfId, setLoadingPdfId] = useState(null);
    const [pdfOverlayVisible, setPdfOverlayVisible] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfUri, setPdfUri] = useState(null);
    const [pdfError, setPdfError] = useState(null);

    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const isEinvoiceActive = Number(selectedCompany?.IsEinvoiceActive) === 1;
    const role = user?.UserCategory || null;
    const params = { userId, userCategory: role, companyId };
    const [openInvoiceDeleteDialog, setOpenInvoiceDeleteDialog] = useState(false);
    const [pendingInvoiceDeleteIds, setPendingInvoiceDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRowData, setMenuRowData] = useState(null);

    //states for e-way bill dialog
    const [ewayDialogOpen, setEwayDialogOpen] = useState(false);
    const [ewayDialogMounted, setEwayDialogMounted] = useState(false);
    const [ewayInvoiceId, setEwayInvoiceId] = useState(null);
    const [ewayBookName, setEwayBookName] = useState(null);

    // states for e-invoice dialog
    const [einvoiceDialogOpen, setEinvoiceDialogOpen] = useState(false);
    const [einvoiceDialogMounted, setEinvoiceDialogMounted] = useState(false);
    const [einvoiceInvoiceId, setEinvoiceInvoiceId] = useState(null);
    const [einvoiceBookName, setEinvoiceBookName] = useState(null);
    const [masterReportData, setMasterReportData] = useState([]);
    const [detailsReportData, setDetailsReportData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);

    const [pdfMenuAnchorEl, setPdfMenuAnchorEl] = useState(null);
    const [pdfInvoiceIdForCopy, setPdfInvoiceIdForCopy] = useState(null);
    const [selectedCopyType, setSelectedCopyType] = useState('Original');
    const [customCopyText, setCustomCopyText] = useState('');

    //states for e-invoice dialog

    const openFilterPopup = useCallback(() => {
        const { from, to } = getFinancialYearRange(new Date());
        const fromDate = filterPopupFromDate || from;
        const toDate = filterPopupToDate || to;
        if (!filterPopupFromDate) setFilterPopupFromDate(fromDate);
        if (!filterPopupToDate) setFilterPopupToDate(toDate);
        setFilterPopupSnapshot({
            mode: filterPopupMode,
            fromDate,
            toDate,
            firms: filterPopupFirms,
            saleAcs: filterPopupSaleAcs,
            series: filterPopupSeries,
            exportCancelledOnly: filterPopupExportCancelledOnly,
        });
        setFilterPopupOpen(true);
    }, [filterPopupExportCancelledOnly, filterPopupFirms, filterPopupFromDate, filterPopupMode, filterPopupSaleAcs, filterPopupSeries, filterPopupToDate]);

    const closeFilterPopup = useCallback(() => setFilterPopupOpen(false), []);

    const handleFilterOk = useCallback(async () => {
        setExportExcelEnabled(true);
        // setFilterPopupOpen(false);  // Keep popup open

        if (!companyId) {
            setSnackbar({
                open: true,
                message: 'Company not selected',
                severity: 'warning'
            });
            return;
        }

        if (!filterPopupFromDate || !filterPopupToDate) {
            setSnackbar({
                open: true,
                message: 'Please select date range',
                severity: 'warning'
            });
            return;
        }

        setReportLoading(true);

        try {
            // ── Prepare filter params ─────────────────────────────────
            const filterParams = {
                companyId,
                fromDate: filterPopupFromDate,
                toDate: filterPopupToDate,
                mode: filterPopupMode,
                // ✅ CHANGE THIS - Send as comma-separated string or leave empty
                firms: filterPopupFirms.length > 0
                    ? filterPopupFirms.map(f => f.id).join(',')
                    : '',
                saleAcs: filterPopupSaleAcs.length > 0
                    ? filterPopupSaleAcs.map(s => s.id).join(',')
                    : '',
                series: filterPopupSeries.length > 0
                    ? filterPopupSeries.map(s => s.id).join(',')
                    : '',
                exportCancelledOnly: filterPopupExportCancelledOnly,
            };

            // ── Call single API ───────────────────────────────────────
            const response = await axios.get(
                `${API_URL}/sale-invoice-register`,
                { params: filterParams }
            );

            const masterData = response?.data?.master?.data || [];
            const detailsData = response?.data?.details?.data || [];

            setMasterReportData(masterData);
            setDetailsReportData(detailsData);

            setSnackbar({
                open: true,
                message: `Report loaded: ${masterData.length} master records, ${detailsData.length} detail records`,
                severity: 'success'
            });

            console.log('Master Report Data:', masterData);
            console.log('Details Report Data:', detailsData);

        } catch (err) {
            console.error('Error fetching report data:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'Failed to fetch report data',
                severity: 'error'
            });
        } finally {
            setReportLoading(false);
        }
    }, [
        companyId,
        filterPopupFromDate,
        filterPopupToDate,
        filterPopupMode,
        filterPopupFirms,
        filterPopupSaleAcs,
        filterPopupSeries,
        filterPopupExportCancelledOnly,
        API_URL
    ]);

    const handleExportExcel = useCallback(() => {
        if (!masterReportData.length && !detailsReportData.length) {
            setSnackbar({ open: true, message: 'No data to export. Please apply filters first.', severity: 'warning' });
            return;
        }

        const formatDate = (val) => {
            if (!val) return '';
            const s = String(val).split('T')[0]; // strip time if present
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
                const [y, m, d] = s.split('-');
                return `${d}-${m}-${y}`;
            }
            return val;
        };

        const buildSheet = (data, columns) => {
            const header = columns.map(c => c.header);
            const rows = data.map(row =>
                columns.map(c => {
                    const val = row[c.key] ?? '';
                    if (c.isDate) return formatDate(val);
                    if (val === 0 || val === '0' || val === 0.0) return '';
                    // Numeric columns often arrive from the API as strings (e.g. SQL
                    // DECIMAL fields serialized as text). aoa_to_sheet writes cells
                    // based on the JS value's type, so a string here becomes a text
                    // cell that Excel's status bar (Sum/Average) will not aggregate.
                    // Coerce to a real number so these columns total correctly.
                    if (c.isNumeric) {
                        const num = Number(val);
                        return Number.isNaN(num) ? val : num;
                    }
                    return val;
                })
            );
            return XLSX.utils.aoa_to_sheet([header, ...rows]);
        };

        // Master: Categoryid & BasicAmt removed; state→State; GSTRate placed before CGSTAmt
        const masterColumns = [
            { key: 'BillDate',        header: 'BillDate',   isDate: true },
            { key: 'BillNumber',      header: 'BillNumber' },
            { key: 'FirmName',        header: 'FirmName' },
            { key: 'GSTNo',           header: 'GSTNo' },
            { key: 'state',           header: 'State' },
            { key: 'StateCode',       header: 'StateCode' },
            { key: 'TransactionType', header: 'TransactionType' },
            { key: 'GSTRate',         header: 'GSTRate',    isNumeric: true },
            { key: 'TaxableAmt',      header: 'TaxableAmt', isNumeric: true },
            { key: 'CGSTAmt',         header: 'CGSTAmt',    isNumeric: true },
            { key: 'SGSTAmt',         header: 'SGSTAmt',    isNumeric: true },
            { key: 'IGSTAmt',         header: 'IGSTAmt',    isNumeric: true },
            { key: 'UTGSTAmt',        header: 'UTGSTAmt',   isNumeric: true },
            { key: 'GSTCEssAmt',      header: 'GSTCessAmt', isNumeric: true },
            { key: 'BillAmt',         header: 'BillAmt',    isNumeric: true },
            { key: 'EwayBillNumber',  header: 'EwayBillNumber' },
            { key: 'Remark',          header: 'Remark' },
        ];

        // Details: exact sequence specified; state→State, itemCode→ItemCode, description→Description, QTY→Qty
        const detailsColumns = [
            { key: 'BillDate',        header: 'BillDate',   isDate: true },
            { key: 'BillNumber',      header: 'BillNumber' },
            { key: 'FirmName',        header: 'FirmName' },
            { key: 'GSTNo',           header: 'GSTNo' },
            { key: 'state',           header: 'State' },
            { key: 'StateCode',       header: 'StateCode' },
            { key: 'TransactionType', header: 'TransactionType' },
            { key: 'itemCode',        header: 'ItemCode' },
            { key: 'description',     header: 'Description' },
            { key: 'HSNNo',           header: 'HSNNo' },
            { key: 'Unit',            header: 'Unit' },
            { key: 'QTY',             header: 'Qty',        isNumeric: true },
            { key: 'Rate',            header: 'Rate',       isNumeric: true },
            { key: 'Disc',            header: 'Disc',       isNumeric: true },
            { key: 'TaxableAmt',      header: 'TaxableAmt', isNumeric: true },
            { key: 'GSTRate',         header: 'GSTRate',    isNumeric: true },
            { key: 'CGSTRate',        header: 'CGSTRate',   isNumeric: true },
            { key: 'SGSTRate',        header: 'SGSTRate',   isNumeric: true },
            { key: 'IGSTRate',        header: 'IGSTRate',   isNumeric: true },
            { key: 'CESSRate',        header: 'CESSRate',   isNumeric: true },
            { key: 'UTGSTRate',       header: 'UTGSTRate',  isNumeric: true },
            { key: 'CGSTAmt',         header: 'CGSTAmt',    isNumeric: true },
            { key: 'SGSTAmt',         header: 'SGSTAmt',    isNumeric: true },
            { key: 'IGSTAmt',         header: 'IGSTAmt',    isNumeric: true },
            { key: 'CESSAmt',         header: 'CESSAmt',    isNumeric: true },
            { key: 'UTGSTAmt',        header: 'UTGSTAmt',   isNumeric: true },
            { key: 'ItemRemark',      header: 'ItemRemark' },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, buildSheet(masterReportData, masterColumns), 'Master');
        XLSX.utils.book_append_sheet(wb, buildSheet(detailsReportData, detailsColumns), 'Details');

        const fromDate = filterPopupFromDate || 'start';
        const toDate = filterPopupToDate || 'end';
        XLSX.writeFile(wb, `SaleInvoiceRegister_${fromDate}_${toDate}.xlsx`);

        setSnackbar({ open: true, message: 'Excel exported successfully!', severity: 'success' });
    }, [masterReportData, detailsReportData, filterPopupFromDate, filterPopupToDate]);

    const cancelFilterPopup = useCallback(() => {
        if (filterPopupSnapshot) {
            setFilterPopupMode(filterPopupSnapshot.mode);
            setFilterPopupFromDate(filterPopupSnapshot.fromDate);
            setFilterPopupToDate(filterPopupSnapshot.toDate);
            setFilterPopupFirms(filterPopupSnapshot.firms || []);
            setFilterPopupSaleAcs(filterPopupSnapshot.saleAcs || []);
            setFilterPopupSeries(filterPopupSnapshot.series || []);
            setFilterPopupExportCancelledOnly(filterPopupSnapshot.exportCancelledOnly);
        }
        setFilterPopupOpen(false);
    }, [filterPopupSnapshot]);

    const handleMenuOpen = (event, row) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuRowData(row);
    };

    // Filter popup data fetchers (FirmName / Sale A/c / Series)
    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!connectionString || !companyId) return;
        let active = true;
        const fetchSeries = async () => {
            try {
                setSeriesLoading(true);
                const { data } = await axios.get(`${API_URL}/bookmaster-series`, {
                    params: { companyId, bookTypeName: 'Sale Challan - Sale Invoice' }
                });
                if (!active) return;
                setSeriesOptions(transform(data, 'BookMastID', 'SeriesName'));
            } catch (err) {
                console.error('Error fetching series:', err);
                if (active) setSeriesOptions([]);
            } finally {
                if (active) setSeriesLoading(false);
            }
        };
        fetchSeries();
        return () => { active = false; };
    }, [companyId, connectionString, filterPopupOpen]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!connectionString || !companyId) return;
        let active = true;
        const fetchAllFirms = async () => {
            try {
                setFirmLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: '', companyId, limit: 1000 } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                console.log('Company search results:', data);
                if (!active) return;
                const opts = data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label);
                setFirmAllOptions(opts);
                if (!(firmInputValue || '').trim()) setFirmOptions(opts);
            } catch (err) {
                console.error('Error fetching FirmName:', err);
                if (!active) return;
                setFirmAllOptions([]);
                setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        };
        fetchAllFirms();
        return () => { active = false; };
    }, [companyId, connectionString, filterPopupOpen]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!connectionString || !companyId) return;
        const q = (firmInputValue || '').trim();
        if (!q) {
            setFirmOptions(firmAllOptions);
            return;
        }
        let active = true;
        const t = setTimeout(async () => {
            try {
                setFirmLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: q, companyId } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                console.log('Company search results:', data);
                if (!active) return;
                setFirmOptions(data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label));
            } catch (err) {
                console.error('Error fetching FirmName:', err);
                if (active) setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        }, 350);
        return () => { active = false; clearTimeout(t); };
    }, [companyId, connectionString, filterPopupOpen, firmAllOptions, firmInputValue]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!connectionString || !companyId) return;
        let active = true;
        const fetchAllSaleAcs = async () => {
            try {
                setSaleAcLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: '', companyId } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                console.log('Company search results:', data);
                if (!active) return;
                const opts = data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label);
                setSaleAcAllOptions(opts);
                if (!(saleAcInputValue || '').trim()) setSaleAcOptions(opts);
            } catch (err) {
                console.error('Error fetching Sale A/c:', err);
                if (!active) return;
                setSaleAcAllOptions([]);
                setSaleAcOptions([]);
            } finally {
                if (active) setSaleAcLoading(false);
            }
        };
        fetchAllSaleAcs();
        return () => { active = false; };
    }, [companyId, connectionString, filterPopupOpen]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!connectionString || !companyId) return;
        const q = (saleAcInputValue || '').trim();
        if (!q) {
            setSaleAcOptions(saleAcAllOptions);
            return;
        }
        let active = true;
        const t = setTimeout(async () => {
            try {
                setSaleAcLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: q, companyId } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                console.log('Company search results:', data);
                if (!active) return;
                setSaleAcOptions(data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label));
            } catch (err) {
                console.error('Error fetching Sale A/c:', err);
                if (active) setSaleAcOptions([]);
            } finally {
                if (active) setSaleAcLoading(false);
            }
        }, 350);
        return () => { active = false; clearTimeout(t); };
    }, [companyId, connectionString, filterPopupOpen, saleAcAllOptions, saleAcInputValue]);

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRowData(null);
    };

    // Open → mount first, then open
    const openEwayDialog = () => {
        setEwayDialogMounted(true);
        setEwayDialogOpen(true);
    };

    // Close → close first, unmount after animation ends
    const closeEwayDialog = () => {
        setEwayDialogOpen(false);
        setTimeout(() => setEwayDialogMounted(false), 300); // matches MUI default transition
    };

    const handleEwayBill = () => {
        if (!menuRowData?.id) return;
        setEwayInvoiceId(menuRowData.id);
        setEwayBookName("SALE");
        openEwayDialog();   // ← use new handler
        handleMenuClose();
    };

    const openEinvoiceDialog = () => {
        setEinvoiceDialogMounted(true);
        setEinvoiceDialogOpen(true);
    };

    const closeEinvoiceDialog = () => {
        setEinvoiceDialogOpen(false);
        setTimeout(() => setEinvoiceDialogMounted(false), 300);
    };

    const handleEInvoice = () => {
        if (!menuRowData?.id) return;
        setEinvoiceInvoiceId(menuRowData.id);
        setEinvoiceBookName("SALE");
        openEinvoiceDialog();
        handleMenuClose();
    };

    // =============== CHALLAN COLUMNS (for DataGrid) ===============
    const challanColumns = [
        {
            field: 'select', headerName: '', width: 40, sortable: false, filterable: false, disableColumnMenu: true,
            renderHeader: () => {
                const referencePartyId = selectedChallanRows.length > 0 ? String(selectedChallanRows[0].PartyID ?? '') : (challanRows[0] ? String(challanRows[0].PartyID ?? '') : null);
                const eligibleRows = referencePartyId ? challanRows.filter(row => String(row.PartyID ?? '') === referencePartyId) : [];
                const allSelected = eligibleRows.length > 0 && eligibleRows.every(row => selectedChallanIds.includes(Number(row.ChallanAutoID)));
                const someSelected = selectedChallanIds.length > 0 && !allSelected;

                return (
                    <Checkbox checked={allSelected} indeterminate={someSelected} disabled={!referencePartyId}
                        onChange={(e) => {
                            if (e.target.checked) {
                                if (!referencePartyId) return;
                                const newSelectedRows = eligibleRows.slice();
                                setSelectedChallanRows(newSelectedRows);
                                setSelectedChallanIds(newSelectedRows.map(r => Number(r.ChallanAutoID)));
                            } else {
                                setSelectedChallanRows([]);
                                setSelectedChallanIds([]);
                            }
                        }}
                    />
                );
            },
            renderCell: ({ row }) => {
                const rowIdNum = Number(row.ChallanAutoID);
                const checked = selectedChallanIds.includes(rowIdNum);
                return (
                    <Checkbox
                        checked={checked}
                        onChange={(e) => {
                            const checkedNow = e.target.checked;
                            setSelectedChallanRows(prevRows => {
                                const prevPartyId = prevRows.length > 0 ? String(prevRows[0].PartyID ?? '') : null;
                                if (checkedNow) {
                                    const rowPartyId = String(row.PartyID ?? '');
                                    if (prevPartyId && rowPartyId !== prevPartyId) {
                                        setSnackbar({ open: true, message: 'Oops! Client name mismatch - select only challans of same client.', severity: 'warning' });
                                        return prevRows;
                                    }
                                    const already = prevRows.some(r => String(r.ChallanAutoID) === String(row.ChallanAutoID));
                                    if (already) return prevRows;
                                    const newRows = [...prevRows, row];
                                    setSelectedChallanIds(newRows.map(r => Number(r.ChallanAutoID)));
                                    return newRows;
                                } else {
                                    const newRows = prevRows.filter(r => Number(r.ChallanAutoID) !== rowIdNum);
                                    setSelectedChallanIds(newRows.map(r => Number(r.ChallanAutoID)));
                                    return newRows;
                                }
                            });
                        }}
                    />
                );
            },
        },
        { field: 'ChallanDate', headerName: 'Challan Date', filterable: true, flex: 0.8, renderCell: (p) => <Typography variant="body2">{p.value || '-'}</Typography> },
        { field: 'ChallanNumber', headerName: 'Challan Number', flex: 1.2 },
        { field: 'RefNo', headerName: 'Ref No.', flex: 1 },
        {
            field: 'clientname', headerName: 'FirmName', flex: 2.5,
            renderCell: (params) => (
                <Box sx={{ cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word', my: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{params.row.clientname || '-'}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{params.row.contactPerson || '-'}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {params.row.mobileNo || '-'}</Typography>
                </Box>
            ),
        },
        {
            field: 'location', headerName: 'Location', filterable: true, flex: 1.5,
            valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(', '),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{params.row.city || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{params.row.state || '-'}</Typography>
                </Box>
            ),
        },
        ...(user?.UserCategory !== "SalesMan" ? [{
            field: "BillAmt",
            headerName: "Total Amt",
            flex: 1,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => {
                const num = Number(params.row.BillAmt);

                if (!Number.isFinite(num)) return "-";

                return `₹${num.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`;
            },
        }] : []),
    ];

    // =============== INVOICE COLUMNS (for DataGrid) ===============
    const baseInvoiceColumns = [
        { field: "BillDate", headerName: "Bill Date", flex: 0.6, filterable: true, },
        {
            field: "BillNumber", headerName: "Bill No", flex: 0.6, align: "center", headerAlign: "center", filterable: true,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate(`/main/v3/InvoiceMaster-edit/${params.row.id}`)} >
                    {params.value ?? "-"}
                </Typography>
            ),
        },
        {
            field: "companyName", headerName: "FirmName", flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>{params.row.companyName || "-"}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{params.row.ContactPerson || "-"}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {params.row.mobileNo || "-"}</Typography>
                </Box>
            ),
        },
        {
            field: "city", headerName: "Location", flex: 0.5, align: "right", headerAlign: "right", filterable: true,
            valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(", "),
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>{params.row.city || "N/A"}</Typography>
                    <Typography variant="body2" color="text.secondary">{params.row.state}</Typography>
                </Box>
            ),
        },
        {
            field: "BillAmt", headerName: "Bill Amt", flex: 0.8, align: "right", headerAlign: "right", filterable: true, type: "number",
            valueGetter: (value) => (value === null || value === undefined || value === "" ? null : Number(value)),
            renderCell: (params) => {
                const num = Number(params.row.BillAmt);
                if (!Number.isFinite(num)) return "-";
                return `₹${num.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`;
            },
        },
        {
            field: "actions", headerName: "Action", flex: 0.3, sortable: false, align: "center", headerAlign: "center", filterable: false,
            renderCell: (params) => {
                const thisId = params.row.id;
                if (loadingPdfId && Number(loadingPdfId) === Number(thisId)) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton color="primary" onClick={(e) => { handlePdfMenuOpen(e, params.row.id); }} >
                            <PrintIcon />
                        </IconButton>

                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)} >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            },
        },
    ];

    const invoiceColumns = useMemo(() => {
        let cols = [...baseInvoiceColumns];
        if (role !== 'User') {
            // Checkbox column
            cols.unshift({
                field: 'select',
                headerName: '',
                width: 48,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderHeader: () => {
                    const allIds = invoiceRows.map(r => Number(r.id));
                    const allSelected = allIds.length > 0 && allIds.every(id => invoiceSelectionModel.includes(id));
                    const someSelected = invoiceSelectionModel.length > 0 && !allSelected;
                    return (
                        <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setInvoiceSelectionModel(allIds);
                                } else {
                                    setInvoiceSelectionModel([]);
                                }
                            }}
                            disabled={invoiceRows.length === 0}
                        />
                    );
                },
                renderCell: (params) => {
                    const idNum = Number(params.row.id);
                    const checked = invoiceSelectionModel.includes(idNum);
                    return (
                        <Checkbox checked={checked} size="small" onChange={(e) => { e.stopPropagation(); setInvoiceSelectionModel(prev => prev.includes(idNum) ? prev.filter(i => i !== idNum) : [...prev, idNum]); }} />
                    );
                },
            });

            // Delete column
            cols.push({
                field: 'delete',
                headerName: '',
                flex: 0.2,
                align: 'right',
                headerAlign: 'right',
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const idNum = Number(params.row.id);
                    const isSelected = invoiceSelectionModel.includes(idNum);
                    return (
                        <Tooltip title={isSelected ? 'Delete selected invoices' : 'Select row to enable delete'}>
                            <span>
                                <IconButton color="error" size="small" disabled={!isSelected} onClick={(e) => { e.stopPropagation(); handleInvoiceDelete(invoiceSelectionModel); }} >
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    );
                },
            });
        }
        return cols;
    }, [invoiceRows, invoiceSelectionModel, role, loadingPdfId]);

    // =============== DATA FETCHING ===============
    const handleCardClick = async () => {
        if (!expanded) {
            setChallanLoading(true);
            try {
                const res = await axios.get(`${API_URL}/dispatch-challans`, { params: { ...params, mode: "ref" } });
                const data = Array.isArray(res.data) ? res.data : [];
                console.log(data);
                const rows = data.map((row, idx) => ({
                    id: row.ChallanId ?? idx,
                    ChallanAutoID: row.ChallanId,
                    ChallanNumber: row.ChallanNumber,
                    RefNo: row.RefNo,
                    ChallanDate: new Date(row.ChallanDate).toLocaleDateString('en-GB'),
                    clientname: row.FirmName,
                    contactPerson: row.ContactPerson,
                    mobileNo: row.MobileNo,
                    city: row.City,
                    state: row.state,
                    BillAmt: Number(row.BillAmt) || 0,
                    PartyID: row.PartyID,
                }));
                setChallanRows(rows);
            } catch (err) {
                console.error('Error fetching challans:', err);
            } finally {
                setChallanLoading(false);
            }
        }
        setExpanded(prev => !prev);
    };

    const fetchInvoiceData = useCallback(async () => {
        setInvoiceLoading(true);
        try {
            const response = await axios.get(`${API_URL}/get-invoice`, { params });
            const data = response.data;
            console.log('data:', data);
            const rows = (Array.isArray(data) ? data : []).map(r => ({
                id: r.SaleInvoiceId,
                BookTypeId: r.BookTypeId ?? null,
                BillDate: r.BillDate ? new Date(r.BillDate).toLocaleDateString('en-GB') : "-",
                BillNumber: r.BillNumber,
                RefNo: r.RefNo || '—',
                companyName: r.clientNameFromMaster,
                ContactPerson: r.clientContactPerson,
                mobileNo: r.clientMobileNo,
                city: r.clientCity,
                state: r.clientState,
                statusName: r.statusName || "Dispatch",
                BillAmt: Number(r.BillAmt) || 0,
            }));
            setInvoiceRows(rows);
        } catch (err) {
            console.error("Error fetching invoices:", err);
        } finally {
            setInvoiceLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvoiceData();
    }, [fetchInvoiceData]);

    // =============== ACTIONS ===============
    const handleInvoice = () => {
        if (canAccess && !canAccess("SaleInvoice", "add")) { showAccessDenied?.(); return; }
        if (selectedChallanRows.length === 0) {
            setSnackbar({ open: true, message: 'No challans selected.', severity: 'warning' });
            return;
        }
        const clientId = selectedChallanRows[0].PartyID;
        const selectedChallanAutoIDs = selectedChallanRows.map(row => row.ChallanAutoID);
        navigate(`/main/v3/InvoiceMaster-stepper-ref/${clientId}`, { state: { selectedChallanAutoIDs } });
    };

    // const handleShareWhatsApp = async () => {
    //     if (!menuRowData?.id) {
    //         setSnackbar({ open: true, message: 'No invoice selected', severity: 'error' });
    //         handleMenuClose();
    //         return;
    //     }
    //     console.log(`menuRowData: ${JSON.stringify(menuRowData)}`);

    //     const saleInvoiceId = menuRowData.id;
    //     const clientPhone = menuRowData.mobileNo;
    //     const companyName = selectedCompany.CompanyName;
    //     const clientName = menuRowData.companyName;
    //     const invoiceNumber = menuRowData.BillNumber;
    //     const amount = menuRowData.BillAmt;

    //     if (!clientPhone) {
    //         setSnackbar({ open: true, message: 'Client phone number not available', severity: 'error' });
    //         handleMenuClose();
    //         return;
    //     }

    //     // Show loading state
    //     setPdfOverlayVisible(true);
    //     setIsGeneratingPdf(true);
    //     setPdfLoading(true);
    //     setPdfError(null);

    //     try {
    //         const userIdSafe = userId || 'usr1';
    //         let formatId = 'format_1';
    //         let bookNameToSend = 'SaleInvoice';
    //         let bookIdToSend = null;
    //         let selectedSeriesId = null;

    //         // ✅ Get book format details
    //         try {
    //             const found = invoiceRows.find(i => Number(i.id) === Number(saleInvoiceId));
    //             if (found?.BookName) bookNameToSend = String(found.BookName).trim();
    //             else if (found?.bookName) bookNameToSend = String(found.bookName).trim();

    //             selectedSeriesId = Number(found?.BookTypeId || 0) || null;
    //         } catch (e) { }

    //         // ✅ Get company format matching
    //         try {
    //             const resp = await axios.get(`${API_URL}/companies`, { params: { companyId } });
    //             const comps = Array.isArray(resp?.data?.companies) ? resp.data.companies : Array.isArray(resp?.data) ? resp.data : [resp.data].filter(Boolean);
    //             const eq = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
    //             let matched = null;
    //             for (const c of comps) {
    //                 if (Array.isArray(c.formats)) {
    //                     for (const f of c.formats) {
    //                         const fBookName = f.BookName || f.bookName;
    //                         const fSeriesId = Number(f.SeriesId || f.seriesId || 0);

    //                         if (
    //                             eq(fBookName, bookNameToSend) &&
    //                             Number(selectedSeriesId || 0) > 0 &&
    //                             fSeriesId === Number(selectedSeriesId)
    //                         ) {
    //                             matched = f;
    //                             break;
    //                         }
    //                     }

    //                     if (matched) break;
    //                 }
    //             }
    //             if (matched) {
    //                 const id = matched.BookID ?? matched.BookId ?? matched.bookId;
    //                 if (id) { formatId = `format_${id}`; bookIdToSend = id; }
    //                 else if (matched.FormName || matched.formName) formatId = String(matched.FormName || matched.formName);
    //                 bookNameToSend = matched.BookName || matched.bookName || bookNameToSend;
    //             }
    //         } catch (err) { console.warn("Format detection failed, using defaults"); }

    //         // ✅ Get company icon/image
    //         let imageBase64 = null;
    //         try {
    //             const iconRes = await axios.get(`${API_URL}/company/GetIcon/${encodeURIComponent(companyId)}`);
    //             if (iconRes?.data?.success && iconRes?.data?.imageBase64) {
    //                 const raw = iconRes.data.imageBase64;
    //                 imageBase64 = typeof raw === 'string' ? raw.split('base64,').pop()?.replace(/\s/g, '') || null : null;
    //             }
    //         } catch (err) { console.warn("Failed to fetch company icon:", err); }

    //         // ✅ Get invoice data
    //         const invoiceResp = await axios.get(`${API_URL}/get-sale-invoice-data/${saleInvoiceId}`, { params: { companyId } });
    //         const { master, details, company } = invoiceResp.data || {};
    //         if (!master) throw new Error("No invoice master data returned");

    //         if (!selectedSeriesId) {
    //             selectedSeriesId = Number(master?.BookTypeId || 0) || null;
    //         }

    //         console.log(invoiceResp);

    //         // ✅ Format invoice items
    //         const items = (details || []).map(d => {
    //             const qty = Number(d.Qty) || 0;
    //             const rate = Number(d.Rate) || 0;
    //             const disc = Number(d.Disc) || 0;
    //             const amount = (qty * rate) - ((qty * rate * disc) / 100);
    //             return {
    //                 hsnCode: d.HSNNo || "",
    //                 ItemCode: d.itemCode,
    //                 description: d.description || "",
    //                 Remark: d.Remark || "",
    //                 qty, rate, Unit: d.Unit || "",
    //                 discount: disc,
    //                 cgst: Number(d.CGST || 0),
    //                 sgst: Number(d.SGST || 0),
    //                 igst: Number(d.IGST || 0),
    //                 amount,
    //             };
    //         });

    //         // ✅ Prepare PDF data
    //         const pdfData = {
    //             companyName: master.FirmName || "",
    //             clientAddress: master.clientAddress || "",
    //             address: master.Address || "",
    //             phone: master.MobileNo || "",
    //             gstNo: master.GSTNo || "",
    //             invoiceNo: master.BillNumber || "",
    //             date: master.BillDate ? normalizeDate(master.BillDate) : "",
    //             transport: master.TransporterName || "SELF",
    //             challanNo: master.lrNumber || "",
    //             poNo: master.RefNo || "",
    //             shipToState: master.consigneeState || "",
    //             shipToStateCode: master.consigneeStateCode || "",
    //             LrNo: master.LRNo || master.LrNo || "",
    //             VehicleNo: master.VehicleNo || "",
    //             LrDate: master.LRDate ? normalizeDate(master.LRDate) : "",
    //             shipToGstNumber: master.consigneeGSTNo || "",
    //             shipToConsigneename: master.ConName || "",
    //             items,
    //             toEmail: master.EmailId || "",
    //             totalAmount: master.grandTotal || 0,
    //             mobileNo: master.MobileNo || "",
    //             basicAmount: Number(master.BasicAmt) || 0,
    //             CGSTAmt: Number(master.CGSTAmt) || 0,
    //             SGSTAmt: Number(master.SGSTAmt) || 0,
    //             IGSTAmt: Number(master.IGSTAmt) || 0,
    //             RoundOff: Number(master.RoundOff) || 0,
    //             BillAmt: Number(master.BillAmt) || 0,
    //             clientState: master.clientState || "",
    //             clientStateCode: master.clientStateCode || "",
    //             imageBase64: imageBase64 || null,
    //             Remark: master.Remark || null,
    //             TCSAmt: master.TCSAmt || null,
    //         };

    //         // ✅ Prepare company header
    //         const companyHeader = company ? {
    //             CompId: companyId,
    //             CompanyName: company.CompanyName || company.companyName || "",
    //             Address: company.Address || company.address || "",
    //             MobileNo: company.MobileNo || company.mobileNo || "",
    //             EmailId: company.EmailId || "",
    //             Website: company.Website || "",
    //             GSTNo: company.GSTNo || company.gstNo || "",
    //             imageBase64: imageBase64 || null,
    //             city: company.City || null,
    //             BkName1: company.BkName1 || null,
    //             BkBranch1: company.BkBranch1 || null,
    //             BkAcNo1: company.BkAcNo1 || null,
    //             BkIFSCCode1: company.BkIFSCCode1 || null,
    //         } : null;

    //         // ✅ STEP 1: Generate PDF and get blob
    //         const pdfResp = await axios.post(
    //             `${PDF_API_BASE}/api/pdf/Invoice/${encodeURIComponent(saleInvoiceId)}`,
    //             { docId: saleInvoiceId, userId: userIdSafe, formatId, data: pdfData, company: companyHeader },
    //             { responseType: "blob" }
    //         );

    //         const blob = pdfResp.data;
    //         if (!blob || blob.size === 0) throw new Error("Empty PDF received");

    //         // ✅ STEP 2: Convert blob to base64 using FileReader
    //         const pdfBase64 = await new Promise((resolve, reject) => {
    //             const reader = new FileReader();
    //             reader.onloadend = () => {
    //                 // reader.result is like "data:application/pdf;base64,JVBERi0x..."
    //                 // Extract only the pure base64 part after the comma
    //                 const base64String = reader.result?.split('base64,').pop()?.replace(/\s/g, '') || null;
    //                 if (base64String) {
    //                     resolve(base64String);
    //                 } else {
    //                     reject(new Error("Failed to convert PDF blob to base64"));
    //                 }
    //             };
    //             reader.onerror = () => reject(new Error("FileReader error while reading PDF blob"));
    //             reader.readAsDataURL(blob);
    //         });

    //         console.log('PDF converted to base64, length:', pdfBase64?.length);

    //         // ✅ STEP 3: Send WhatsApp message with base64 PDF data
    //         const whatsappPayload = {
    //             invoiceId: saleInvoiceId,
    //             clientName: clientName,
    //             companyName: companyName,
    //             clientPhone: clientPhone,
    //             invoiceNumber: invoiceNumber,
    //             amount: amount,
    //             pdfBuffer: pdfBase64, // ← Send base64 instead of URL
    //         };

    //         console.log(whatsappPayload)

    //         console.log('Sending WhatsApp message with base64 PDF data');

    //         const whatsappResp = await axios.post(
    //             'http://192.168.1.248:4002/api/invoice/send-whatsapp',
    //             whatsappPayload
    //         );

    //         if (whatsappResp?.data?.success) {
    //             setSnackbar({
    //                 open: true,
    //                 message: `✅ Invoice sent to ${clientPhone} via WhatsApp`,
    //                 severity: 'success'
    //             });
    //             console.log('WhatsApp message sent successfully');
    //         } else {
    //             throw new Error(whatsappResp?.data?.message || 'Failed to send via WhatsApp');
    //         }

    //     } catch (err) {
    //         console.error("Error:", err);

    //         let errorMessage = "Failed to send Invoice via WhatsApp";

    //         if (err.response?.status === 400) {
    //             errorMessage = err.response?.data?.message || "Invalid request - check phone number and invoice data";
    //         } else if (err.response?.status === 500) {
    //             errorMessage = err.response?.data?.message || "Server error - please try again";
    //         } else if (err.message) {
    //             errorMessage = err.message;
    //         }

    //         setSnackbar({
    //             open: true,
    //             message: errorMessage,
    //             severity: 'error'
    //         });
    //     } finally {
    //         setPdfOverlayVisible(false);
    //         setIsGeneratingPdf(false);
    //         setPdfLoading(false);
    //         handleMenuClose();
    //     }
    // };

    const handleInvoiceView = async (saleInvoiceId) => {
        if (isGeneratingPdf) return;
        setPdfOverlayVisible(true);
        setIsGeneratingPdf(true);
        setPdfLoading(true);
        setPdfError(null);

        if (!saleInvoiceId) {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
            return;
        }

        const userIdSafe = userId || 'usr1';
        let formatId = 'format_1';
        let bookNameToSend = 'SaleInvoice';
        let bookIdToSend = null;
        let selectedSeriesId = null;
        

        try {
            const found = invoiceRows.find(i => Number(i.id) === Number(saleInvoiceId));
            if (found?.BookName) bookNameToSend = String(found.BookName).trim();
            else if (found?.bookName) bookNameToSend = String(found.bookName).trim();

            selectedSeriesId = Number(found?.BookTypeId || 0) || null;
        } catch (e) { console.error("Failed to extract book name from invoice row:", e); }

        // ✅ Fetch fresh format with Signature and QRCode from companies API (with cache-buster)
        let matchedFormat = null;
        try {
            // Add timestamp to bypass server-side cache and get fresh data
            const cacheBreaker = new Date().getTime();
            const resp = await axios.get(`${API_URL}/companies`, {
                params: { companyId, t: cacheBreaker },
                timeout: 15000
            });
            const comps = Array.isArray(resp?.data?.companies) ? resp.data.companies : Array.isArray(resp?.data) ? resp.data : [resp.data].filter(Boolean);
            const eq = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

            // Find the matching format that contains Signature and QRCode
            for (const c of comps) {
                if (Array.isArray(c.formats)) {
                    for (const f of c.formats) {
                        const fBookName = f.BookName || f.bookName;
                        const fSeriesId = Number(f.SeriesId || f.seriesId || 0);

                        if (
                            eq(fBookName, bookNameToSend) &&
                            Number(selectedSeriesId || 0) > 0 &&
                            fSeriesId === Number(selectedSeriesId)
                        ) {
                            matchedFormat = f;
                            console.log("seriesId:", fSeriesId, fBookName, matchedFormat);
                            break;
                        }
                    }

                    if (matchedFormat) break;
                }
            }
            if (matchedFormat) {
                const id = matchedFormat.BookID ?? matchedFormat.BookId ?? matchedFormat.bookId;
                if (id) { formatId = `format_${id}`; bookIdToSend = id; }
                else if (matchedFormat.FormName || matchedFormat.formName) formatId = String(matchedFormat.FormName || matchedFormat.formName);
                bookNameToSend = matchedFormat.BookName || matchedFormat.bookName || bookNameToSend;
                console.log('✅ [PDF] Fresh format with Signature/QRCode fetched from companies API');
            }
        } catch (err) { console.warn("Failed to fetch fresh companies data:", err.message); }

        let imageBase64 = null;
        let signatureBase64 = null;
        try {
            const iconRes = await axios.get(`${API_URL}/company/GetIcon/${encodeURIComponent(companyId)}`);
            if (iconRes?.data?.success && iconRes?.data?.imageBase64) {
                const raw = iconRes.data.imageBase64;
                imageBase64 = typeof raw === 'string' ? raw.split('base64,').pop()?.replace(/\s/g, '') || null : null;
            }
            if (iconRes?.data?.signatureBase64) {
                signatureBase64 = iconRes.data.signatureBase64;
            }
        } catch (err) { console.warn("Failed to fetch company icon:", err); }

        // ✅ Get Signature from the matched format object (fresh from API, not cached)
        if (!matchedFormat?.Signature) signatureBase64 = null;

        try {
            const invoiceResp = await axios.get(`${API_URL}/get-sale-invoice-data/${saleInvoiceId}`, { params: { companyId } });
            const { master, details, company, termsAndConditions, hsnSummary, additionalChargeItems } = invoiceResp.data || {};
            if (!master) throw new Error("No invoice master data returned");

            console.log("data:", invoiceResp.data);
            const termsText = Array.isArray(termsAndConditions) && termsAndConditions.length > 0
                ? termsAndConditions
                    .sort((a, b) => a.SrNo - b.SrNo)
                    .map(t => `# ${t.TermsDescription}`)
                    .join('\n')
                : null;

            if (!selectedSeriesId) {
                selectedSeriesId = Number(master?.BookTypeId || 0) || null;
            }

            console.log(invoiceResp);

            const items = (details || []).map(d => {
                const qty = Number(d.Qty) || 0;
                const rate = Number(d.Rate) || 0;
                const disc = Number(d.Disc) || 0;
                const amount = (qty * rate) - ((qty * rate * disc) / 100);
                return {
                    hsnCode: d.HSNNo || "",
                    ItemCode: d.itemCode,
                    description: d.description || "",
                    Remark: d.Remark || "",
                    qty, rate, Unit: d.Unit || "",
                    discount: disc,
                    cgst: Number(d.CGST || 0),
                    sgst: Number(d.SGST || 0),
                    igst: Number(d.IGST || 0),
                    amount,
                    TaxableAmt: Number(d.TaxableAmt) || 0,
                    pkg: d.Pkg || "",
                };
            });

            // Additional Charges — bound directly from saleinvoicemaster's
            // Adc1Amt..Adc5Amt columns (already present on `master`); the name
            // for each slot comes from productmaster.description where
            // TransType = 199 + slot (200-204), returned as additionalChargeItems.
            const additionalCharges = [1, 2, 3, 4, 5]
                .map(slot => {
                    const chargeItem = (additionalChargeItems || []).find(ci => Number(ci.TransType) === 199 + slot);
                    return {
                        name: chargeItem?.description || `Charge ${slot}`,
                        amount: Number(master[`Adc${slot}Amt`]) || 0,
                    };
                })
                .filter(c => c.amount > 0);

                console.log("additionalCharges", additionalCharges);

            // ✅ Generate QR Code with UPIID and BillAmt (only if QRCode setting is enabled)
            let qrCodeBase64 = null;

            // Check if QRCode is enabled from the matched format object (fresh from API)
            // Explicitly set to null if QRCode = 0, otherwise generate if enabled
            if (matchedFormat?.QRCode === 0 || matchedFormat?.QRCode === false) {
                qrCodeBase64 = null;
                console.log('[QR Code] QRCode disabled (QRCode = 0) for Sale Invoice - sending null');
            } else if (matchedFormat?.QRCode === 1 || matchedFormat?.QRCode === true) {
                // Generate QR Code only if enabled and UPIID exists
                if (selectedCompany?.UPIID && master.BillAmt) {
                    try {
                        const billAmount = (Number(master.BillAmt) || 0).toFixed(2);
                        const upiString = `upi://pay?pa=${selectedCompany.UPIID}&pn=${encodeURIComponent(company.CompanyName || company.companyName || "")}&am=${billAmount}&tn=Invoice`;
                        qrCodeBase64 = await QRCode.toDataURL(upiString, { errorCorrectionLevel: 'H', width: 80, margin: 0 });
                        console.log('[QR Code] Generated successfully');
                    } catch (qrErr) {
                        console.error('[QR Code] Generation failed:', qrErr);
                        qrCodeBase64 = null;
                    }
                } else {
                    console.log('[QR Code] Cannot generate - missing UPIID or BillAmt');
                    qrCodeBase64 = null;
                }
            } else {
                qrCodeBase64 = null;
                console.log('[QR Code] QRCode setting not found in format - sending null');
            }

            // ✅ E-Invoice QR: gstinfo.INVQRDetails is either an already-rendered QR image
            // (base64 / data URL) or the signed QR token text. Either way the PDF gets a
            // QR code image: an image is used as-is, a token is converted into a QR code.
            let invQrCodeBase64 = null;
            const rawInvQr = String(master.INVQRDetails || "").trim();
            if (rawInvQr) {
                if (rawInvQr.startsWith("data:image")) {
                    invQrCodeBase64 = rawInvQr;
                } else if (rawInvQr.startsWith("iVBORw0KGgo")) {
                    invQrCodeBase64 = `data:image/png;base64,${rawInvQr}`;
                } else if (rawInvQr.startsWith("/9j/")) {
                    invQrCodeBase64 = `data:image/jpeg;base64,${rawInvQr}`;
                } else {
                    try {
                        invQrCodeBase64 = await QRCode.toDataURL(rawInvQr, { errorCorrectionLevel: 'L', width: 150, margin: 0 });
                    } catch (qrErr) {
                        console.error('[E-Invoice QR] Generation failed:', qrErr);
                        invQrCodeBase64 = null;
                    }
                }
            }

            const pdfData = {
                companyName: master.FirmName || "",
                clientAddress: master.clientAddress || "",
                clientCity: master.clientCity || "",
                address: master.Address || "",
                shipFromAddress: master.consignerAddress || "",
                phone: master.MobileNo || "",
                gstNo: master.GSTNo || "",
                invoiceNo: master.BillNumber || "",
                date: master.BillDate ? normalizeDate(master.BillDate) : "",
                challanDate: master.ChallanDate ? normalizeDate(master.ChallanDate) : "",
                poDate: master.PODate ? normalizeDate(master.PODate) : "",
                ewayBillNo: master.EwayBillNumber || "",
                // E-Invoice details (IRN / ACK from saleinvoicemaster, QR from gstinfo.INVQRDetails)
                IRNNo: master.IRNNo || "",
                IRNDate: master.IRNDate ? normalizeDate(master.IRNDate) : "",
                ACKNo: master.ACKNo || "",
                ACKDate: master.ACKDate ? normalizeDate(master.ACKDate) : "",
                // INVQRDetails converted into a QR code image (data URL) for the PDF
                INVQRDetails: invQrCodeBase64,
                transport: master.TransporterName || "SELF",
                challanNo: master.ChallanNo || "",
                poNo: master.PONo || "",
                shipToState: master.consigneeState || "",
                shipFromState: master.consignerState || "",
                shipToStateCode: master.consigneeStateCode || "",
                shipFromStateCode: master.consignerStatecode || "",
                LrNo: master.LRNo || master.LrNo || "",
                VehicleNo: master.VehicleNo || "",
                LrDate: master.LRDate ? normalizeDate(master.LRDate) : "",
                shipToGstNumber: master.consigneeGSTNo || "",
                shipFromGstNumber: master.consignerGSTNo || "",
                clientGstNumber: master.clientGSTNo || "",
                shipToConsigneename: master.ConName || "",
                shipFromConsigneename: master.consignerConName || "",
                items,
                toEmail: master.EmailId || "",
                totalAmount: master.grandTotal || 0,
                mobileNo: master.MobileNo || "",
                basicAmount: Number(master.TaxableAmt) || 0,
                additionalCharges,
                CGSTAmt: Number(master.CGSTAmt) || 0,
                SGSTAmt: Number(master.SGSTAmt) || 0,
                IGSTAmt: Number(master.IGSTAmt) || 0,
                RoundOff: Number(master.RoundOff) || 0,
                BillAmt: Number(master.BillAmt) || 0,
                clientState: master.clientState || "",
                clientStateCode: master.clientStateCode || "",
                imageBase64: imageBase64 || null,
                Remark: master.Remark || null,
                TCSAmt: master.TCSAmt || null,
                termsAndConditions: termsText,
                hsnSummary: hsnSummary || [],
                // E-Invoice enabled for the company (companymaster.IsEinvoiceActive, from AuthContext)
                isEinvoiceActive: isEinvoiceActive,
                copyType: selectedCopyType || "Original",
                customCopyText: selectedCopyType === 'Other' ? customCopyText : selectedCopyType === 'Original' ? 'Original for Recipient' : selectedCopyType,
                PaymentTerm: master.DueDays || null,
            };

            const companyHeader = company ? {
                CompId: companyId,
                CompanyName: company.CompanyName || company.companyName || "",
                Address: company.Address || company.address || "",
                MobileNo: company.MobileNo || company.mobileNo || "",
                EmailId: company.EmailId || "",
                Website: company.Website || "",
                GSTNo: company.GSTNo || company.gstNo || "",
                imageBase64: imageBase64 || null,
                signatureBase64: signatureBase64 || null,
                qrCodeBase64: qrCodeBase64 || null,
                // ✅ Add color from matched format (default black if not set)
                headerColor: matchedFormat?.Color || '#000000',
                city: company.City || null,
                BkName1: company.BkName1 || null,
                BkBranch1: company.BkBranch1 || null,
                BkAcNo1: company.BkAcNo1 || null,
                BkIFSCCode1: company.BkIFSCCode1 || null,
                MSMENo: company.MSMENo || null
            } : null;

            const pdfResp = await axios.post(
                `${PDF_API_BASE}/api/pdf/Invoice/${encodeURIComponent(saleInvoiceId)}`,
                { docId: saleInvoiceId, userId: userIdSafe, formatId, data: pdfData, company: companyHeader },
                { responseType: "blob" }
            );

            console.log(companyHeader);

            const blob = pdfResp.data;
            if (!blob || blob.size === 0) throw new Error("Empty PDF received");
            const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

            // (old code : commented to implement pdf view in webview)
            // const newWin = window.open(blobUrl, "_blank");
            // if (newWin) {
            //     newWin.focus();
            //     const timer = setInterval(() => {
            //         if (newWin.closed) { URL.revokeObjectURL(blobUrl); clearInterval(timer); }
            //     }, 1000);
            // } else {
            //     if (pdfUri) URL.revokeObjectURL(pdfUri);
            //     setPdfUri(blobUrl);
            //     setSnackbar({ open: true, message: "Popup blocked – showing PDF below", severity: "warning" });
            // }

            // (new code : Added to handle pdf in web and mobile both using webview)
            if (window.ReactNativeWebView) {

                const reader = new FileReader();

                reader.onloadend = () => {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "OPEN_PDF",
                            base64: reader.result
                        })
                    );
                };

                reader.readAsDataURL(blob);

            } else {

                const newWin = window.open(blobUrl, "_blank");
                if (newWin) {
                    newWin.focus();
                    const timer = setInterval(() => {
                        if (newWin.closed) { URL.revokeObjectURL(blobUrl); clearInterval(timer); }
                    }, 1000);
                } else {
                    if (pdfUri) URL.revokeObjectURL(pdfUri);
                    setPdfUri(blobUrl);
                    setSnackbar({ open: true, message: "Popup blocked – showing PDF below", severity: "warning" });
                }
            }
        } catch (err) {
            console.error("Invoice PDF Generation Failed:", err);
            setSnackbar({ open: true, message: "Failed to generate Invoice PDF", severity: "error" });
        } finally {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
        }
    };

    const handleInvoiceDelete = (objAryId) => {
        if (canAccess && !canAccess("SaleInvoice", "delete")) { showAccessDenied?.(); return; }
        const arr = Array.isArray(objAryId) ? objAryId : [objAryId];

        const numericIds = arr
            .map(v => Number(v))
            .filter(n => !isNaN(n));

        if (!numericIds.length) return;

        setPendingInvoiceDeleteIds(numericIds);
        setOpenInvoiceDeleteDialog(true);
    };

    const handlePdfMenuOpen = (event, invoiceId) => {
        event.stopPropagation();
        setPdfMenuAnchorEl(event.currentTarget);
        setPdfInvoiceIdForCopy(invoiceId);
        setSelectedCopyType('Original');
        setCustomCopyText('');
    };

    const handlePdfMenuClose = () => {
        setPdfMenuAnchorEl(null);
        setPdfInvoiceIdForCopy(null);
        setSelectedCopyType('Original');
        setCustomCopyText('');
    };

    const handlePrintPdf = () => {
        if (!pdfInvoiceIdForCopy) return;
        handlePdfMenuClose();
        handleInvoiceView(pdfInvoiceIdForCopy);
    };


    const confirmInvoiceDelete = async (objAryId) => {
        const numericIds = pendingInvoiceDeleteIds;
        if (!numericIds.length || deleting) return;

        setDeleting(true);

        try {
            const payload = { objAryId: numericIds, companyId };
            const { data } = await axios.post(`${API_URL}/delete-invoices`, payload);

            const deletedIds = Array.isArray(data.deletedIds) && data.deletedIds.length > 0
                ? data.deletedIds.map(Number).filter(n => !isNaN(n))
                : (data.deletedInvoices > 0 ? numericIds : []);

            const referencedIds = Array.isArray(data.referencedIds)
                ? data.referencedIds.map(Number).filter(n => !isNaN(n))
                : [];

            if (deletedIds.length > 0) {
                setInvoiceRows(prev => prev.filter(row => !deletedIds.includes(Number(row.id))));
                setInvoiceSelectionModel(prev => prev.filter(id => !deletedIds.includes(Number(id))));
            }

            if (deletedIds.length > 0 && referencedIds.length === 0) {
                setSnackbar({ open: true, message: `Deleted Successfully.`, severity: 'success' });
            } else if (deletedIds.length > 0 && referencedIds.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            } else if (referencedIds.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            } else {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            }
        } catch (err) {
            console.error('Error deleting invoices:', err);
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            setSnackbar({ open: true, message: serverMsg ? `Failed: ${serverMsg}` : `Failed to delete invoices`, severity: 'error' });
        } finally {
            setDeleting(false);
            setOpenInvoiceDeleteDialog(false);
            setPendingInvoiceDeleteIds([]);
        }
    };

    // =============== FILTERED DATA ===============
    const filteredInvoices = useMemo(() => {
        if (!filterText) return formatRows(invoiceRows);
        const lower = filterText.toLowerCase();
        return formatRows(invoiceRows.filter(row =>
            String(row.BillNumber || "").toLowerCase().includes(lower) ||
            String(row.companyName || "").toLowerCase().includes(lower)
        ));
    }, [invoiceRows, filterText]);

    const filteredChallans = useMemo(() => {
        if (!filterText || !expanded) return formatRows(challanRows);
        const lower = filterText.toLowerCase();
        const filtered = challanRows.filter(row =>
            String(row.ChallanNumber || "").toLowerCase().includes(lower) ||
            String(row.clientname || "").toLowerCase().includes(lower)
        )
        return formatRows(filtered);
    }, [challanRows, filterText, expanded]);

    // =============== MOBILE CARD RENDERERS ===============
    // =============== MOBILE CARD RENDERERS ===============
    const renderInvoiceCards = () => {
        if (invoiceLoading) return <Typography sx={{ p: 4, textAlign: 'center' }}>Loading invoices...</Typography>;
        if (filteredInvoices.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredInvoices.map((row) => {
            const isSelected = invoiceSelectionModel.includes(row.id);
            const avatarColor = stringToColor(row.companyName || 'Invoice');
            const initials = getInitials(row.companyName);
            const locationText = [row.city, row.state].filter(Boolean).join(', ') || '—';

            const toggleSelection = (e) => {
                e.stopPropagation();
                setInvoiceSelectionModel(prev =>
                    prev.includes(row.id) ? prev.filter(i => i !== row.id) : [...prev, row.id]
                );
            };

            const hasReference = Boolean(row.RefNo && row.RefNo !== '—');

            return (
                <Card
                    key={row.id}
                    onClick={() => navigate(`/main/v3/InvoiceMaster-edit/${row.id}`)}
                    sx={{
                        mb: 2, borderLeft: `6px solid ${hasReference ? '#FF9800' : '#1976d2'}`, borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: 'background.paper', cursor: 'pointer',
                        '&:hover': { boxShadow: 6 },
                    }}
                >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        {/* Top Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                {role !== 'User' && (
                                    <Checkbox checked={isSelected} onChange={toggleSelection} onClick={(e) => e.stopPropagation()} size="small" />
                                )}
                                <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36, flexShrink: 0 }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                        {row.companyName || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {locationText}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap>
                                {row.BillDate}
                            </Typography>
                        </Box>

                        {/* Middle: Bill No + Ref No */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.BillNumber || '—'}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 0.8 }}>
                                {hasReference ? (
                                    <Chip label={`Ref: ${row.RefNo}`} size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 500 }} />
                                ) : (
                                    <Chip label="Manual" size="small" sx={{ bgcolor: '#e7f5ff', color: "#0067bb" }} />
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Bottom: Contact + Phone + View */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0, flex: 1, flexWrap: "wrap" }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 18, color: '#FF9800', mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {row.ContactPerson || 'N/A'}
                                    </Typography>
                                </Box>
                                {row.mobileNo && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PhoneIcon sx={{ fontSize: 18, color: '#10b981', mr: 1, flexShrink: 0 }} />
                                        <Typography variant="body2" fontWeight={600} color="primary.main" noWrap>
                                            {row.mobileNo}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <IconButton
                                color="primary"
                                onClick={(e) => {
                                    handlePdfMenuOpen(e, row.id);
                                }}
                                sx={{ flexShrink: 0 }}
                            >
                                <PrintIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMenuOpen(e, row);
                                }}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    const renderChallanCards = () => {
        if (challanLoading) return <Typography sx={{ p: 4, textAlign: 'center' }}>Loading challans...</Typography>;
        if (filteredChallans.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredChallans.map((row) => {
            const checked = selectedChallanIds.includes(Number(row.ChallanAutoID));
            const avatarColor = stringToColor(row.clientname || 'Challan');
            const initials = getInitials(row.clientname);
            const locationText = [row.city, row.state].filter(Boolean).join(', ') || '—';

            return (
                <Card key={row.ChallanAutoID} sx={{ mb: 2, borderLeft: '6px solid #ff9800', borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: 'background.paper', }} >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        {/* Top Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                <Checkbox
                                    checked={checked}
                                    onChange={(e) => {
                                        const checkedNow = e.target.checked;
                                        setSelectedChallanRows(prevRows => {
                                            const prevPartyId = prevRows.length > 0 ? String(prevRows[0].PartyID ?? '') : null;
                                            if (checkedNow) {
                                                const rowPartyId = String(row.PartyID ?? '');
                                                if (prevPartyId && rowPartyId !== prevPartyId) {
                                                    setSnackbar({ open: true, message: 'Client mismatch!', severity: 'warning' });
                                                    return prevRows;
                                                }
                                                if (prevRows.some(r => r.ChallanAutoID === row.ChallanAutoID)) return prevRows;
                                                const newRows = [...prevRows, row];
                                                setSelectedChallanIds(newRows.map(r => Number(r.ChallanAutoID)));
                                                return newRows;
                                            } else {
                                                const newRows = prevRows.filter(r => Number(r.ChallanAutoID) !== Number(row.ChallanAutoID));
                                                setSelectedChallanIds(newRows.map(r => Number(r.ChallanAutoID)));
                                                return newRows;
                                            }
                                        });
                                    }}
                                    size="small"
                                />
                                <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36, flexShrink: 0 }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                        {row.clientname || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {locationText}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap>
                                {row.ChallanDate}
                            </Typography>
                        </Box>

                        {/* Middle: Challan No + Amount */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.ChallanNumber || '—'}
                                </Typography>
                            </Box>
                            {user?.UserCategory !== "SalesMan" && (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    ₹{Number(row.BillAmt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Bottom: Contact + Mobile */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", }} >
                            {/* LEFT SIDE */}
                            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                <PersonIcon sx={{ fontSize: 18, color: "#FF9800", mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.contactPerson || "N/A"}
                                </Typography>
                            </Box>

                            {/* RIGHT SIDE */}
                            {row.mobileNo && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <PhoneIcon sx={{ fontSize: 18, color: "#10b981", mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" fontWeight={600} color="primary.main" noWrap >
                                        {row.mobileNo}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    return (
        <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 } }}>
            {/* ── Module Header ──────────────────────────────────────────────
                If sale challan rows are selected, the Add button creates a
                Sale Invoice from those challans; else opens a blank form.
            ────────────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)', borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Sale Invoices
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<FilterAltIcon />} onClick={openFilterPopup}>Filter</Button>
                        {/* Toggle linked challans panel */}
                        <Button variant="contained" size="small" onClick={handleCardClick}>{expanded ? "Hide" : "Show"} Challan</Button>
                        {/* If challan rows selected → create invoice from them; else open blank form */}
                        <Button variant="contained" size="small" startIcon={<AddIcon />}
                            onClick={() => {
                                if (selectedChallanRows.length > 0) {
                                    handleInvoice();
                                } else {
                                    navigate("/main/v3/InvoiceMaster-stepper");
                                }
                            }}
                        >
                            {isSmallDevice ? 'Add' : 'Add Sale Invoice'}
                        </Button>
                    </Box>
                </Paper>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 } }}>
                    {/* Mobile Search */}
                    {!isDesktop && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <TextField fullWidth size="small" placeholder={expanded ? "Search Challan..." : "Search Invoice..."} value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>, }} variant="outlined" />
                        </Box>
                    )}


                    {/* Mobile Bulk Delete Bar (Invoices) */}
                    {!isDesktop && role !== 'User' && invoiceSelectionModel.length > 0 && !expanded && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                            <Box display="flex" alignItems="center">
                                <Checkbox
                                    checked={
                                        filteredInvoices.length > 0 &&
                                        filteredInvoices.every(r =>
                                            invoiceSelectionModel.includes(r.id)
                                        )
                                    }
                                    onChange={() => {
                                        const allSelected =
                                            filteredInvoices.length > 0 &&
                                            filteredInvoices.every(r =>
                                                invoiceSelectionModel.includes(r.id)
                                            );

                                        if (allSelected) {
                                            setInvoiceSelectionModel([]);
                                        } else {
                                            setInvoiceSelectionModel(
                                                filteredInvoices.map(r => r.id)
                                            );
                                        }
                                    }}
                                />

                                <Typography>
                                    {invoiceSelectionModel.length} selected
                                </Typography>
                            </Box>
                            <Tooltip title="Delete selected">
                                <IconButton onClick={() => handleInvoiceDelete(invoiceSelectionModel)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Challan Section */}
                    {expanded && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            {/* <Box my={1}> */}

                            {isDesktop ? (
                                <>
                                    <Paper elevation={2} sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden', mb: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                            <Typography variant="h6" fontWeight={600}>Challan List</Typography>
                                        </Box>
                                        <Box sx={{ height: '71vh', width: '100%' }}>
                                            {challanLoading ? <CenterLoader /> : filteredChallans?.length === 0 ? <NoDataComponent /> :
                                                <DataGrid rows={filteredChallans} columns={challanColumns} getRowHeight={() => 'auto'} getRowId={(row) => row.ChallanAutoID} pageSizeOptions={[50, 100]}
                                                    sx={{
                                                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                        '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },
                                                        '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                                        '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },
                                                        '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                                        '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                                        '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                                                        '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                                                        '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                                    }}
                                                    slots={{
                                                        noResultsOverlay: CustomNoDataOverlay
                                                    }}
                                                />}
                                        </Box>
                                    </Paper>
                                </>
                            ) : (
                                <Paper sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="h6" fontWeight={600}>Challan List</Typography>
                                    </Box>
                                    {challanRows?.length === 0 ? <NoDataComponent /> : renderChallanCards()}
                                </Paper>
                            )}
                            {/* </Box> */}
                        </Collapse>
                    )}

                    {/* Invoice List */}
                    <Box>
                        {isDesktop ? (
                            <Paper elevation={2} sx={{ borderRadius: 3, p: 0.5, overflow: 'hidden' }}>
                                {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="h6" fontWeight={600}>Invoice List</Typography>
                                </Box> */}
                                <Box sx={{ height: '71vh', width: '100%' }}>
                                    {invoiceLoading ? <CenterLoader /> : filteredInvoices?.length === 0 ? <NoDataComponent /> :

                                        <DataGrid rows={filteredInvoices} getRowId={(row) => row.id} getRowHeight={() => "auto"} pageSizeOptions={[50, 100]} disableRowSelectionOnClick columns={invoiceColumns}
                                            sx={{
                                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },
                                                '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                                '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },
                                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                                '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                                                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                                                '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                            }}
                                            slots={{
                                                noResultsOverlay: CustomNoDataOverlay
                                            }}
                                        />}
                                </Box>
                            </Paper>
                        ) : (
                            <Paper sx={{ borderRadius: 3, p: 0.5, overflow: 'hidden', display: !expanded ? 'block' : 'none' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="h6" fontWeight={600} >Invoice List</Typography>
                                </Box>
                                {invoiceRows?.length === 0 ? <NoDataComponent /> : renderInvoiceCards()}
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Overlays & Snackbar */}
            {pdfLoading && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                    <Typography>Preparing PDF...</Typography>
                </Box>
            )}

            {pdfOverlayVisible && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center', bgcolor: '#111', p: '40px 60px', borderRadius: '20px' }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: '#fff' }} />
                        <Typography variant="h6" sx={{ color: '#fff', mt: 3 }}>Generating Invoice PDF…</Typography>
                        <Typography variant="body2" sx={{ color: '#aaa', mt: 1 }}>Please wait</Typography>
                    </Box>
                </Box>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant='filled'>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ──────────────────────────── */}
            <Dialog open={openInvoiceDeleteDialog} onClose={() => { if (!deleting) setOpenInvoiceDeleteDialog(false); }} maxWidth="xs" fullWidth>
                {deleting
                    ? <LinearProgress color="error" sx={{ height: 4 }} />
                    : <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingInvoiceDeleteIds.length}</Typography>
                        {' '}sale invoice(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setOpenInvoiceDeleteDialog(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" size="small" disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                        onClick={confirmInvoiceDelete}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} >
                <MenuItem onClick={handleEwayBill}>
                    E-Way Bill
                </MenuItem>
                {isEinvoiceActive && (<MenuItem onClick={handleEInvoice}>
                    E-Invoice
                </MenuItem>)}
                {/* <MenuItem onClick={handleShareWhatsApp}>
                    Share WhatsApp
                </MenuItem> */}
            </Menu>

            {/* Filter Popup */}
            <Dialog open={filterPopupOpen} onClose={cancelFilterPopup} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }} >
                {/* Header */}
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, pt: 2, px: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }} >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
                            <FilterListIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                            Filter Invoices
                        </Typography>
                    </Box>
                    <IconButton onClick={cancelFilterPopup} size="small"
                        sx={{
                            color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 3, py: 2, bgcolor: '#fafafa' }}>
                    {/* Filter Mode Selection */}
                    <Paper elevation={0} sx={{ p: 2, mb: 1, mt: 1, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
                                Filter Mode
                            </Typography>
                        </Box>
                        <RadioGroup row value={filterPopupMode} onChange={(e) => setFilterPopupMode(e.target.value)} sx={{ display: 'flex', gap: 2 }} >
                            <FormControlLabel value="datewise"
                                control={
                                    <Radio size="small"
                                        sx={{
                                            '&.Mui-checked': { color: 'primary.main', }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            📅 Datewise
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1, m: 0, bgcolor: filterPopupMode === 'datewise' ? '#e3f2fd' : '#f5f5f5', borderRadius: 1.5, px: 2, py: 0.5, border: filterPopupMode === 'datewise' ? '2px solid' : '2px solid transparent', borderColor: 'primary.main', transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: filterPopupMode === 'datewise' ? '#e3f2fd' : '#eeeeee', }
                                }}
                            />
                            <FormControlLabel
                                value="partywise"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            👥 Partywise
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1, m: 0, bgcolor: filterPopupMode === 'partywise' ? '#e3f2fd' : '#f5f5f5', borderRadius: 1.5, px: 2, py: 0.5, border: filterPopupMode === 'partywise' ? '2px solid' : '2px solid transparent', borderColor: 'primary.main', transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: filterPopupMode === 'partywise' ? '#e3f2fd' : '#eeeeee', }
                                }}
                            />
                        </RadioGroup>
                    </Paper>

                    {/* Filter Fields */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Date Range */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#ff6b6b', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    📆 Date Range
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                                        From Date
                                    </Typography>
                                    <TextField fullWidth size="small" type="date" value={filterPopupFromDate} onChange={(e) => setFilterPopupFromDate(e.target.value)} InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa', borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                                        To Date
                                    </Typography>
                                    <TextField fullWidth size="small" type="date" value={filterPopupToDate} onChange={(e) => setFilterPopupToDate(e.target.value)} InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa', borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Paper>

                        {/* FirmName */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#4ecdc4', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    🏢 FirmName
                                </Typography>
                            </Box>
                            <Autocomplete multiple disableCloseOnSelect options={firmOptions} loading={firmLoading} value={filterPopupFirms} onChange={(_, newValue) => setFilterPopupFirms(newValue)} inputValue={firmInputValue} onInputChange={(_, v) => setFirmInputValue(v)} getOptionLabel={(o) => o?.label || ''} isOptionEqualToValue={(o, v) => String(o?.id ?? o?.label) === String(v?.id ?? v?.label)} renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={selected} size="small"
                                            sx={{
                                                mr: 1, color: '#4ecdc4',
                                                '&.Mui-checked': { color: '#4ecdc4' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" placeholder={filterPopupFirms?.length ? `${filterPopupFirms.length} firm(s) selected` : 'Select firms (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa', borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupFirms?.length > 0 && (
                                                        <Chip label={`${filterPopupFirms.length}`} size="small" sx={{ ml: 0.5, height: 22, bgcolor: '#4ecdc4', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>

                        {/* Sale A/c */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#f7b731', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    💰 Sale Account
                                </Typography>
                            </Box>
                            <Autocomplete multiple disableCloseOnSelect options={saleAcOptions} loading={saleAcLoading} value={filterPopupSaleAcs} onChange={(_, newValue) => setFilterPopupSaleAcs(newValue)} inputValue={saleAcInputValue} onInputChange={(_, v) => setSaleAcInputValue(v)} getOptionLabel={(o) => o?.label || ''} isOptionEqualToValue={(o, v) => String(o?.id ?? o?.label) === String(v?.id ?? v?.label)} renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={selected} size="small"
                                            sx={{
                                                mr: 1, color: '#f7b731',
                                                '&.Mui-checked': { color: '#f7b731' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" placeholder={filterPopupSaleAcs?.length ? `${filterPopupSaleAcs.length} account(s) selected` : 'Select accounts (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa', borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupSaleAcs?.length > 0 && (
                                                        <Chip label={`${filterPopupSaleAcs.length}`} size="small" sx={{ ml: 0.5, height: 22, bgcolor: '#f7b731', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>

                        {/* Series */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#a55eea', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    📋 Series
                                </Typography>
                            </Box>
                            <Autocomplete multiple disableCloseOnSelect options={seriesOptions} loading={seriesLoading} value={filterPopupSeries} onChange={(_, newValue) => setFilterPopupSeries(newValue)} getOptionLabel={(o) => o?.label || ''} isOptionEqualToValue={(o, v) => String(o?.id ?? o?.label) === String(v?.id ?? v?.label)} renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={selected} size="small"
                                            sx={{
                                                mr: 1, color: '#a55eea',
                                                '&.Mui-checked': { color: '#a55eea' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" placeholder={filterPopupSeries?.length ? `${filterPopupSeries.length} series selected` : 'Select series (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa', borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupSeries?.length > 0 && (
                                                        <Chip label={`${filterPopupSeries.length}`} size="small" sx={{ ml: 0.5, height: 22, bgcolor: '#a55eea', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>

                        {/* Export Cancelled Bill Only */}
                        <Paper elevation={0} sx={{ px: 2, py: 1, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} >
                            <FormControlLabel
                                control={
                                    <Checkbox checked={filterPopupExportCancelledOnly} onChange={(e) => setFilterPopupExportCancelledOnly(e.target.checked)}
                                        sx={{
                                            color: '#ee5a6f',
                                            '&.Mui-checked': { color: '#ee5a6f' }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            ❌ Export Cancelled Bill Only
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    </Box>
                </DialogContent>

                {/* Action Buttons */}
                <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0', gap: 2 }} >
                    <Button onClick={cancelFilterPopup} variant="outlined"
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1, borderColor: '#d0d0d0', color: 'text.secondary',
                            '&:hover': { borderColor: '#999', bgcolor: '#f5f5f5' }
                        }}
                    >
                        ✕ Cancel
                    </Button>

                    {/* ADD THIS NEW BUTTON */}
                    <Button
                        onClick={handleExportExcel}
                        variant="outlined"
                        disabled={!exportExcelEnabled}
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1,
                            '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                    >
                        📊 Export Excel
                    </Button>

                    <Button
                        onClick={handleFilterOk}
                        variant="contained"
                        disabled={reportLoading}  // ADD THIS
                        startIcon={reportLoading ? <CircularProgress size={16} /> : null}  // ADD THIS
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        ✓ {reportLoading ? 'Loading...' : 'Apply Filters'}  {/* MODIFY THIS */}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for e-way bill */}
            <Dialog open={ewayDialogOpen} onClose={(event, reason) => { if (reason === 'backdropClick') return; closeEwayDialog(); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxWidth: 700 } }} >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                    E-Way Bill
                    <IconButton onClick={closeEwayDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {ewayDialogMounted && (
                        <EwayBillMaster
                            connectionString={connectionString}
                            invoiceId={ewayInvoiceId}
                            bookName={ewayBookName}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog for e-invoice */}
            <Dialog open={einvoiceDialogOpen && isEinvoiceActive} onClose={(event, reason) => { if (reason === 'backdropClick') return; closeEinvoiceDialog(); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxWidth: 700 } }} >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                    E-Invoice
                    <IconButton onClick={closeEinvoiceDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {einvoiceDialogMounted && (
                        <EInvoiceMaster
                            connectionString={connectionString}
                            invoiceId={einvoiceInvoiceId}
                            bookName={einvoiceBookName}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Popover for PDF Copy Selection */}
            <Popover
                open={Boolean(pdfMenuAnchorEl)}
                anchorEl={pdfMenuAnchorEl}
                onClose={handlePdfMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
                        Select Copy Type
                    </Typography>
                    <RadioGroup value={selectedCopyType} onChange={(e) => setSelectedCopyType(e.target.value)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControlLabel
                                value="Original"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Original</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Original' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Duplicate copy"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Duplicate copy</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Duplicate copy' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Triplicate copy"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Triplicate copy</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Triplicate copy' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Transport copy"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Transport copy</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Transport copy' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Other"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Other</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Other' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            {selectedCopyType === 'Other' && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Enter custom type"
                                    value={customCopyText}
                                    onChange={(e) => {
                                        if (e.target.value.length <= 15) {
                                            setCustomCopyText(e.target.value);
                                        }
                                    }}
                                    inputProps={{ maxLength: 15 }}
                                    helperText={`${customCopyText.length}/15`}
                                    sx={{ mt: 1, mb: 1 }}
                                />
                            )}
                        </Box>
                    </RadioGroup>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={handlePdfMenuClose}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePrintPdf}
                            variant="contained"
                            size="small"
                            fullWidth
                            startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
                            sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                        >
                            Open
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
}
